import { PublishCommand, PublishCommandInput, PublishCommandOutput, SNSClient } from '@aws-sdk/client-sns';
import { logger } from 'src/analytics/KatalLogger';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { getAWSServiceCredentials } from './auth-token-services';

/**
 * Valid event types for SNS notifications.
 * Note: If changed, update in slack_notification_handler as well
 */
export type EventType = 'info' | 'success' | 'error';

/**
 * Environment configuration for SNS service
 */
const SNS_CONFIG = {
  REGION: ENVIRONMENT_VARIABLES.env.Region,
  ARN: ENVIRONMENT_VARIABLES.env.SNSNotificationARN,
  STAGE: ENVIRONMENT_VARIABLES.env.Stage
} as const;

/**
 * Stage prefix mapping for SNS notification titles
 */
const STAGE_PREFIX_MAP: Record<string, string> = {
  dev: '[xPT-Local] ',
  alpha: '[xPT-Alpha] ',
  beta: '[xPT-Beta] ',
  prod: ''
} as const;

/**
 * Interface for SNS notification parameters
 */
interface SNSNotificationParams {
  subject: string;
  message: string;
  eventType: EventType;
}

/**
 * Creates and returns an SNS client instance with proper configuration
 * @returns Promise<SNSClient> Configured SNS client
 */
const createSNSClient = async (): Promise<SNSClient> => {
  const credentials = await getAWSServiceCredentials();
  return new SNSClient({
    region: SNS_CONFIG.REGION,
    credentials
  });
};

/**
 * Gets the title prefix based on the current environment stage
 * @returns string Environment-specific prefix for notification titles
 */
const getTitlePrefix = (): string => {
  return STAGE_PREFIX_MAP[SNS_CONFIG.STAGE.toLowerCase()] || '';
};

/**
 * Creates the SNS publish command input
 * @param params SNS notification parameters
 * @returns PublishCommandInput Configured command input
 */
const createPublishCommandInput = (params: SNSNotificationParams): PublishCommandInput => {
  const { subject, message, eventType } = params;
  return {
    TopicArn: SNS_CONFIG.ARN,
    Subject: `${getTitlePrefix()}${subject}`,
    Message: message,
    MessageAttributes: {
      EventType: {
        DataType: 'String',
        StringValue: eventType
      }
    }
  };
};

/**
 * Sends an SNS notification with the provided parameters
 * @param params SNS notification parameters
 * @returns Promise<void>
 */
export const sendSNSNotification = async (subject: string, message: string, eventType: EventType): Promise<void> => {
  try {
    if (!SNS_CONFIG.ARN) {
      logger.warn('SNS notification skipped: No ARN configured');
      return;
    }

    const snsClient = await createSNSClient();
    const commandInput = createPublishCommandInput({ subject, message, eventType });
    const publishCommand = new PublishCommand(commandInput);

    const result: PublishCommandOutput = await snsClient.send(publishCommand);

    logger.info('SNS Notification sent successfully', {
      messageId: result.MessageId,
      subject,
      eventType
    });
  } catch (error) {
    logger.error('Failed to send SNS Notification', {
      error,
      subject,
      eventType
    });
  }
};
